/**
 * 在全景画面上点击放置热点（yaw/pitch），体验对齐 720yun 类产品
 * 注意：嵌套在管理弹窗内，须等 Modal afterOpenChange 后再建 Pannellum，否则容器未挂载会黑屏。
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Space } from 'antd';
import { message } from '../../utils/antdMessage';
import {
  authPanoramaImageUrl,
  destroyPannellumViewer,
  loadPannellum
} from '../../utils/pannellumLoader';
import '../OrthoToolbox.css';

async function waitForEl(getEl, tries = 40) {
  for (let i = 0; i < tries; i += 1) {
    const el = getEl();
    if (el && el.clientWidth > 0 && el.clientHeight > 0) return el;
    await new Promise((r) => setTimeout(r, 50));
  }
  return getEl();
}

export default function PanoramaHotspotPlaceModal({
  open,
  imageUrl,
  fromSceneName = '',
  toSceneName = '',
  hotspotTitle = '',
  onCancel,
  onConfirm
}) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const dragRef = useRef({ x: 0, y: 0, moved: false });
  const listenersRef = useRef({ onClick: null, onDown: null, onMove: null });
  const [modalReady, setModalReady] = useState(false);
  const [picked, setPicked] = useState(null); // { yaw, pitch }
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!open) {
      setModalReady(false);
      setPicked(null);
      setLoadError('');
      destroyPannellumViewer(viewerRef);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !modalReady || !imageUrl) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError('');
      setPicked(null);
      try {
        const el = await waitForEl(() => containerRef.current);
        if (cancelled || !el) {
          setLoadError('全景容器未就绪，请关闭后重试');
          return;
        }

        const pannellum = await loadPannellum();
        if (cancelled || !containerRef.current) return;

        destroyPannellumViewer(viewerRef);
        // 清掉旧监听
        const prev = listenersRef.current;
        if (prev.onDown) el.removeEventListener('mousedown', prev.onDown);
        if (prev.onMove) el.removeEventListener('mousemove', prev.onMove);
        if (prev.onClick) el.removeEventListener('click', prev.onClick);

        containerRef.current.innerHTML = '';

        const panorama = authPanoramaImageUrl(imageUrl);
        viewerRef.current = pannellum.viewer(containerRef.current, {
          type: 'equirectangular',
          panorama,
          autoLoad: true,
          showControls: true,
          compass: true,
          hfov: 100,
          hotSpots: [],
          // 大图加载失败时给出提示
          onLoadFailure: () => {
            if (!cancelled) {
              setLoadError('全景图加载失败（请确认场景已上传且可预览）');
              message.error('全景图加载失败');
            }
          }
        });

        // 部分版本无 onLoadFailure 配置，再兜底探测
        try {
          viewerRef.current.on('error', (err) => {
            if (cancelled) return;
            setLoadError(String(err || '全景加载错误'));
          });
        } catch (_) { /* ignore */ }

        const onDown = (e) => {
          dragRef.current = { x: e.clientX, y: e.clientY, moved: false };
        };
        const onMove = (e) => {
          const d = dragRef.current;
          if (!d) return;
          if (Math.abs(e.clientX - d.x) > 4 || Math.abs(e.clientY - d.y) > 4) {
            d.moved = true;
          }
        };
        const onClick = (e) => {
          if (dragRef.current?.moved) return;
          const viewer = viewerRef.current;
          if (!viewer?.mouseEventToCoords) return;
          try {
            const coords = viewer.mouseEventToCoords(e);
            const pitch = Number(coords?.[0]);
            const yaw = Number(coords?.[1]);
            if (!Number.isFinite(pitch) || !Number.isFinite(yaw)) return;
            setPicked({ pitch, yaw });
            try {
              viewer.removeHotSpot('zhxt-place-preview');
            } catch (_) { /* ignore */ }
            viewer.addHotSpot({
              id: 'zhxt-place-preview',
              pitch,
              yaw,
              type: 'info',
              text: hotspotTitle || toSceneName || '热点',
              cssClass: 'zhxt-pano-hotspot'
            });
          } catch (err) {
            message.warning(err.message || '取点失败');
          }
        };

        listenersRef.current = { onClick, onDown, onMove };
        el.addEventListener('mousedown', onDown);
        el.addEventListener('mousemove', onMove);
        el.addEventListener('click', onClick);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message || '全景加载失败');
          message.error(e.message || '全景加载失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      const el = containerRef.current;
      const prev = listenersRef.current;
      if (el) {
        if (prev.onDown) el.removeEventListener('mousedown', prev.onDown);
        if (prev.onMove) el.removeEventListener('mousemove', prev.onMove);
        if (prev.onClick) el.removeEventListener('click', prev.onClick);
      }
      listenersRef.current = { onClick: null, onDown: null, onMove: null };
      destroyPannellumViewer(viewerRef);
    };
  }, [open, modalReady, imageUrl, hotspotTitle, toSceneName]);

  return (
    <Modal
      open={open}
      title={`在画面上放置热点：${fromSceneName || '当前场景'} → ${toSceneName || '目标场景'}`}
      onCancel={onCancel}
      width="90vw"
      style={{ top: 20 }}
      zIndex={1200}
      destroyOnClose
      maskClosable={false}
      afterOpenChange={(visible) => {
        setModalReady(!!visible);
      }}
      footer={(
        <Space>
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            disabled={!picked}
            onClick={() => {
              if (!picked) return;
              onConfirm?.(picked);
            }}
          >
            确认放置
          </Button>
        </Space>
      )}
    >
      <div style={{ marginBottom: 8, color: '#475569', fontSize: 13 }}>
        拖动可环视；在目标位置<strong>单击</strong>放置热点（文案：{hotspotTitle || toSceneName || '热点'}）。
        {picked
          ? ` 已选 yaw=${picked.yaw.toFixed(1)} / pitch=${picked.pitch.toFixed(1)}`
          : ' 尚未选点'}
      </div>
      {!imageUrl && (
        <div style={{ color: '#b91c1c', marginBottom: 8 }}>缺少场景图片地址，请先上传全景并刷新项目。</div>
      )}
      {loadError && (
        <div style={{ color: '#b91c1c', marginBottom: 8 }}>{loadError}</div>
      )}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '68vh',
          minHeight: 360,
          background: '#111',
          borderRadius: 8,
          overflow: 'hidden',
          position: 'relative'
        }}
      />
      {loading && (
        <div style={{ marginTop: 8, color: '#64748b', fontSize: 12 }}>加载全景中…</div>
      )}
    </Modal>
  );
}
